import { useState } from 'react';
import { TextSection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DynamicFieldAttributes, DynamicFieldIconSmall, RTEditor } from '@frontend/design-system';
import { COLOR_PICKER_PALETTE } from '../../content-composer/constants';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import { ExpansionButton } from '../atoms/expansion-button';
import { PopoverButtonGroup } from '../atoms/popover-button-group';
import { ToolbarButtonGroup } from '../atoms/toolbar-button-group';
import { RTEOptionsToolbar } from './rte-options-toolbar';

const LARGE_BREAKPOINT = 1450;
const MEDIUM_BREAKPOINT = 850;
const SMALL_BREAKPOINT = 600;

type SelectedToolbar = 'rte' | 'options';

type Props = {
  children: React.ReactNode;
};

export const RTERoot = ({ children }: Props) => {
  const { sharedDynamicFieldProps, textDynamicFields } = useContentComposer();
  const { id, props, setProps } = useComposerSection<TextSection>();
  const initialHTML = props.text;

  const [selectedToolbar, setSelectedToolbar] = useState<SelectedToolbar>('rte');
  const [selectedDynamicField, setSelectedDynamicField] = useState<DynamicFieldAttributes | undefined>();

  const handleCustomSelect = (dynamicField: DynamicFieldAttributes) => {
    setSelectedDynamicField(dynamicField);
    setSelectedToolbar('options');
  };

  return (
    <RTEditor
      dynamicFields={textDynamicFields}
      hasDraggableBlocks={false}
      hideFloatingToolbar
      initialHTML={initialHTML}
      onHTMLChange={(html) => setProps((props) => (props.text = html))}
      saveToLocalStorage={false}
      sharedDynamicFieldProps={sharedDynamicFieldProps}
      uniqueId={`content-section-${id}`}
    >
      {children}
      <SectionBase.Toolbar
        disableResponsiveness
        css={{
          width: '100%',
        }}
        position='bottom-center'
      >
        {selectedToolbar === 'options' ? (
          <RTEOptionsToolbar
            css={{
              margin: 0,
              padding: 0,
              width: 'initial',
            }}
            selectedDynamicField={selectedDynamicField}
            selectRTEToolbar={() => setSelectedToolbar('rte')}
          />
        ) : (
          <RTEditor.Toolbar>
            <RTEditor.Toolbar.FontPicker />
            <ToolbarButtonGroup breakpoint={SMALL_BREAKPOINT}>
              <RTEditor.Toolbar.FontSize />
            </ToolbarButtonGroup>
            <ToolbarButtonGroup breakpoint={MEDIUM_BREAKPOINT}>
              <RTEditor.Toolbar.FontColor palette={COLOR_PICKER_PALETTE} />
              <RTEditor.Toolbar.Bold />
              <RTEditor.Toolbar.Underline />
              <RTEditor.Toolbar.Italic />
            </ToolbarButtonGroup>
            <ToolbarButtonGroup breakpoint={LARGE_BREAKPOINT}>
              <RTEditor.Toolbar.ElementFormat />
              <RTEditor.Toolbar.EmojiPicker />
              <RTEditor.Toolbar.Link />
              {textDynamicFields && (
                <RTEditor.Toolbar.DynamicField
                  fields={textDynamicFields}
                  noTransform
                  onCustomSelect={handleCustomSelect}
                />
              )}
            </ToolbarButtonGroup>
            <ExpansionButton breakpoint={LARGE_BREAKPOINT}>
              <PopoverButtonGroup breakpoint={SMALL_BREAKPOINT}>
                <RTEditor.Toolbar.FontSize />
              </PopoverButtonGroup>
              <PopoverButtonGroup breakpoint={MEDIUM_BREAKPOINT}>
                <RTEditor.Toolbar.FontColor palette={COLOR_PICKER_PALETTE} />
                <RTEditor.Toolbar.Bold />
                <RTEditor.Toolbar.Underline />
                <RTEditor.Toolbar.Italic />
              </PopoverButtonGroup>
              <RTEditor.Toolbar.ElementFormat />
              <RTEditor.Toolbar.EmojiPicker />
              <RTEditor.Toolbar.Link />
              {textDynamicFields && (
                <RTEditor.Toolbar.DynamicField
                  buttonLabel={<DynamicFieldIconSmall />}
                  fields={textDynamicFields}
                  noTransform
                  onCustomSelect={handleCustomSelect}
                />
              )}
            </ExpansionButton>
          </RTEditor.Toolbar>
        )}
      </SectionBase.Toolbar>
    </RTEditor>
  );
};
